import { useState } from 'react';
import { ArrowForward, Call, Email, ExpandMore, PersonAddAlt1, WhatsApp, WorkOutline } from "@mui/icons-material";
import { Box, Menu, MenuItem } from "@mui/material";

/* =====================================================================
   COLORES Y ENLACES GENERALES
   Si cambia un color de la marca o un link, se cambia solo aquí.
   ===================================================================== */
const COLORES = {
    verde: "#8CF23A",        // Verde Traffic Light (barras, botones, logo)
    verdeOscuro: "#4E9A12",  // Color de los links al pasar el mouse
    verdeClaro: "#F2FCE8",   // Fondo del botón "Conoce más" al pasar el mouse
    texto: "#1A1A1A",        // Color principal del texto
    fondoPagina: "#2A2D36",  // Fondo oscuro detrás de la tarjeta
    grisLogo: "#3A3A3A",     // Color del logo grande (tarjetas sin foto)
};

const LINKS = {
    sitio: "https://trafficlight.mx/",
    vacantes: "https://trafficlight.mx/bolsa_trabajo",
    linkedin: "https://www.linkedin.com/company/trafficlight-de-mexico",
    facebook: "https://www.facebook.com/trafficlightdemexico",
    instagram: "https://www.instagram.com/trafficlightdemexico/#",
};

/* =====================================================================
   FIGURA DEL LOGO "TL"
   El logo se dibuja con rectángulos. Se usa grande y gris en el centro
   del encabezado cuando NO hay foto. (El logo de arriba es un SVG de public/.)
   ===================================================================== */
const LOGO_GRANDE = { ancho: 154, alto: 150, piezas: [
    { left: 0, top: 0, width: 68, height: 18 },
    { left: 50, top: 0, width: 18, height: 122 },
    { left: 86, top: 24, width: 68, height: 18 },
    { left: 86, top: 24, width: 20, height: 126 },
]};

const FiguraTL = ({ logo, color }) => (
    <Box sx={{ position: "relative", width: logo.ancho, height: logo.alto }}>
        {logo.piezas.map((pieza, i) => (
            <Box key={i} sx={{ position: "absolute", backgroundColor: color, ...pieza }} />
        ))}
    </Box>
);

/* =====================================================================
   BOTÓN REDONDO (WhatsApp, Guardar contacto)
   Si recibe "href" funciona como link; si recibe "onClick", como botón.
   ===================================================================== */
const BotonCircular = ({ titulo, href, onClick, children }) => (
    <Box
        component={href ? "a" : "button"}
        href={href}
        onClick={onClick}
        target={href ? "_blank" : undefined}
        rel={href ? "noreferrer" : undefined}
        title={titulo}
        aria-label={titulo}
        sx={{
            width: 38, height: 38, borderRadius: "50%", cursor: "pointer",
            border: `1px solid ${COLORES.texto}`, backgroundColor: "#FFFFFF", color: COLORES.texto,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
            transition: "all .2s",
            // Al pasar el mouse se pinta de verde
            '&:hover': { backgroundColor: COLORES.verde, borderColor: COLORES.verde, color: COLORES.texto },
        }}
    >
        {children}
    </Box>
);

/* =====================================================================
   TARJETA PRINCIPAL
   Props:
   - nombre, celular, correo: datos de la persona (celular "VACIO" = no tiene).
   - puesto: clave para buscar el puesto en español e inglés (ver "puestos").
   - foto (opcional): ruta de la foto, p. ej. "/static/FotosPerfiles/GustavoRios.jpg".
     Con foto se usa el diseño con foto; sin foto, el diseño con logo gris.
   - linkedin (opcional): lista de LinkedIn extra de la persona, p. ej.
     [{ nombre: "Personal", url: "https://www.linkedin.com/in/..." }].
     Si la tiene, al tocar "LinkedIn" se abre un menú con esas opciones
     más el LinkedIn de TrafficLight. Si no, "LinkedIn" abre directo el de la empresa.
   - vacantes (opcional): true = muestra la franja "Únete a nuestro equipo" (bolsa de trabajo).
   ===================================================================== */
const CardMain = ({ nombre, puesto, celular, correo, foto, linkedin, vacantes }) => {
    // true mientras el mouse está sobre el puesto (se muestra en inglés)
    const [enIngles, setEnIngles] = useState(false);
    // Elemento donde se abre el menú de LinkedIn (null = menú cerrado)
    const [menuLinkedin, setMenuLinkedin] = useState(null);

    // Puestos de cada persona: puesto_1 = español, puesto_2 = inglés
    const puestos = {
        JoseMiguelRivas: {
            puesto_1: "Director Comercial",
            puesto_2: "Commercial Director",
        },
        GustavoRios: {
            puesto_1: "CEO",
            puesto_2: "CEO",
        },
        SergioRios: {
            puesto_1: "DCEO",
            puesto_2: "DCEO",
        },
        JanineSafawi: {
            puesto_1: "Gerente Comercial",
            puesto_2: "Commercial Manager",
        },
        JesusCastillo: {
            puesto_1: "Gestor Comercial",
            puesto_2: "Commercial Manager",
        },
        MontserratMontero: {
            puesto_1: "Gerente de Finanzas Estratégicas",
            puesto_2: "Strategic Finance Manager",
        },
        AdrianaMeraz: {
            puesto_1: "Gerente de Desarrollo de Negocios",
            puesto_2: "Business Development Manager",
        },
        ArielRuiz:{
            puesto_1:"Responsable del Centro de Evaluación y Optimización de Negocios",
            puesto_2: "Head of Business Assessment and Optimization Center"

        },
        LuceroJimenez:{
            puesto_1:"Generalista de Atracción de Talento y Cultura Organizacional",
            puesto_2: "Talent Acquisition and Organizational Culture Generalist"

        },
        AdrianLima:{
            puesto_1: "Analista de Gestión de Talento",
            puesto_2: "Talent Management Analyst"
        },
        SeleneGutierrez:{
            puesto_1: "Auxiliar Comunicación Interna",
            puesto_2: "Internal Communications Assistant"
        }
    };

    // Datos ya preparados para mostrarse
    const datosPuesto = puestos[puesto] || { puesto_1: "", puesto_2: "" };
    const tieneCelular = celular && celular !== "VACIO";
    const correoLimpio = (correo || "").trim();            // quita espacios sobrantes
    const celularSoloNumeros = tieneCelular ? celular.replace(/\D/g, "") : "";
    // LinkedIn de la persona + el de la empresa al final (solo si tiene LinkedIn propios)
    const opcionesLinkedin = linkedin && linkedin.length > 0
        ? [...linkedin, { nombre: "TrafficLight", url: LINKS.linkedin }]
        : [];

    const saveVCard = () => {
        // Definir la información del contacto en formato vCard
        const vCardData =
            "BEGIN:VCARD\n" +
            "VERSION:3.0\n" +
            "FN:"+nombre+"\n" +                                           // Nombre completo
            (tieneCelular ? "TEL;TYPE=mobile:"+celular+"\n" : "") +       // Teléfono móvil (si tiene)
            "EMAIL:"+correoLimpio+"\n" +                                  // Correo electrónico
            "ORG:TrafficLight\n" +                                        // Nombre de la empresa
            "TITLE:"+datosPuesto.puesto_1.trim()+"\n" +                   // Cargo
            "URL:https://trafficlight.mx\n" +                             // Página web
            "END:VCARD";

        // Crear un Blob con el contenido de la vCard
        const blob = new Blob([vCardData], { type: "text/vcard" });
        const url = URL.createObjectURL(blob);

        // Crear un enlace invisible para descargar el archivo
        const a = document.createElement("a");
        a.href = url;
        a.download = "contacto.vcf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    // Estilo de los links de texto (teléfono, correo, redes sociales)
    const estiloLink = {
        color: COLORES.texto, textDecoration: "none", display: "flex", alignItems: "center", gap: "7px",
        '&:hover': { color: COLORES.verdeOscuro },
    };

    return(
        /* ---------- FONDO DE LA PÁGINA ----------
           Los valores { xs: ..., sm: ... } cambian según el tamaño de pantalla:
           - xs = celular (menos de 600px): sin fondo oscuro, la tarjeta ocupa toda la pantalla.
           - sm = tablet/computadora: fondo oscuro con la tarjeta centrada.
           "100dvh" = alto real visible en el celular (sin contar las barras del navegador). */
        <Box sx={{ minHeight: "100dvh", display: "flex", justifyContent: "center", boxSizing: "border-box",
            backgroundColor: { xs: "#FFFFFF", sm: COLORES.fondoPagina },
            alignItems: { xs: "stretch", sm: "center" },
            padding: { xs: 0, sm: "40px 16px" },
            fontFamily: "'Ubuntu', sans-serif", WebkitFontSmoothing: "antialiased",
        }}>
            {/* ---------- TARJETA BLANCA ----------
                En celular: ancho completo, sin esquinas redondeadas ni sombra. */}
            <Box sx={{ width: { xs: "100%", sm: 340 }, maxWidth: { xs: 480, sm: "100%" }, overflow: "hidden",
                backgroundColor: "#FFFFFF", display: "flex", flexDirection: "column",
                borderRadius: { xs: 0, sm: "22px" },
                boxShadow: { xs: "none", sm: "0 24px 60px rgba(0,0,0,.35)" },
            }}>

                {/* ---------- ENCABEZADO: con foto o sin foto ---------- */}
                <Box sx={{ position: "relative", height: foto ? 400 : 340,
                    // Sin foto: degradado gris de oscuro (arriba) a blanco (abajo)
                    // Con foto: fondo blanco (se ve arriba de la foto, que está recorrida hacia abajo)
                    background: foto ? "#FFFFFF"
                        : "linear-gradient(180deg,#3A3A3A 0%,#8A8A8A 30%,#E4E4E4 62%,#FFFFFF 100%)",
                }}>
                    {foto ? (
                        <>
                            {/* Foto de la persona, bajada 40px para que el logo no tape la cabeza */}
                            <Box component="img" src={foto} alt={nombre}
                                sx={{ width: "100%", height: "calc(100% - 40px)", marginTop: "40px",
                                    objectFit: "cover", objectPosition: "top", display: "block" }} />
                            {/* Sombra blanca abajo de la foto (se une con la tarjeta) */}
                            <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none",
                                background: "linear-gradient(180deg,rgba(255,255,255,0) 80%,rgba(255,255,255,.7) 94%,#FFFFFF 100%)",
                            }} />
                        </>
                    ) : (
                        /* Logo grande gris en el centro, en lugar de la foto */
                        <Box sx={{ position: "absolute", left: "50%", top: 118, transform: "translateX(-50%)" }}>
                            <FiguraTL logo={LOGO_GRANDE} color={COLORES.grisLogo} />
                        </Box>
                    )}

                    {/* Logo "TrafficLight - Designing emotions"
                        - Con foto: logo negro (public/static/logos/TL-Negro.svg) sobre el fondo blanco.
                        - Sin foto: logo blanco (public/static/logos/TL-Blanco.svg) sobre el degradado gris. */}
                    <Box component="img" src={process.env.PUBLIC_URL + (foto ? "/static/logos/TL-Negro.svg" : "/static/logos/TL-Blanco.svg")}
                        alt="TrafficLight - Designing emotions"
                        sx={{ position: "absolute", left: "50%", top: 18, transform: "translateX(-50%)",
                            width: 170, display: "block", pointerEvents: "none" }} />
                </Box>

                {/* ---------- NOMBRE Y PUESTO ---------- */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
                    padding: "6px 28px 0", gap: "10px",
                }}>
                    <Box sx={{ fontSize: 24, fontWeight: 700, color: COLORES.texto, letterSpacing: ".3px",
                        lineHeight: 1.05, textTransform: "uppercase",
                    }}>
                        {nombre}
                    </Box>

                    {/* Puesto entre dos barras verdes. Al pasar el mouse cambia a inglés */}
                    <Box
                        onMouseEnter={() => setEnIngles(true)}
                        onMouseLeave={() => setEnIngles(false)}
                        sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "2px", maxWidth: 284, cursor: "default" }}
                    >
                        <Box sx={{ width: 4, alignSelf: "stretch", backgroundColor: COLORES.verde, borderRadius: "2px", flex: "0 0 auto" }} />
                        <Box sx={{ fontSize: 12.5, fontWeight: 500, color: COLORES.texto, lineHeight: 1.45, textWrap: "balance" }}>
                            {enIngles ? datosPuesto.puesto_2 : datosPuesto.puesto_1}
                        </Box>
                        <Box sx={{ width: 4, alignSelf: "stretch", backgroundColor: COLORES.verde, borderRadius: "2px", flex: "0 0 auto" }} />
                    </Box>
                </Box>

                {/* ---------- TELÉFONO Y CORREO (texto) ---------- */}
                <Box sx={{ margin: "26px 28px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: "11px", fontSize: 13 }}>
                    {/* El teléfono solo se muestra si la persona tiene celular */}
                    {tieneCelular && (
                        <Box component="a" href={"tel:"+celular} sx={estiloLink}>
                            <Call sx={{ fontSize: 17 }} />{celular}
                        </Box>
                    )}
                    <Box component="a" href={"mailto:"+correoLimpio} sx={estiloLink}>
                        <Email sx={{ fontSize: 17 }} />{correoLimpio}
                    </Box>
                </Box>

                {/* ---------- BOTONES REDONDOS: WhatsApp y Guardar contacto ---------- */}
                <Box sx={{ margin: "26px 28px 0", display: "flex", justifyContent: "center", gap: "16px" }}>
                    {/* WhatsApp solo si la persona tiene celular */}
                    {tieneCelular && (
                        <BotonCircular titulo="WhatsApp" href={"https://wa.me/"+celularSoloNumeros}>
                            <WhatsApp sx={{ fontSize: 17 }} />
                        </BotonCircular>
                    )}
                    <BotonCircular titulo="Guardar contacto" onClick={saveVCard}>
                        <PersonAddAlt1 sx={{ fontSize: 17 }} />
                    </BotonCircular>
                </Box>

                {/* ---------- REDES SOCIALES (texto) ---------- */}
                <Box sx={{ margin: "22px 28px 0", display: "flex", justifyContent: "center", gap: "22px",
                    fontSize: 11.5, fontWeight: 500, letterSpacing: ".5px",
                }}>
                    {opcionesLinkedin.length > 0 ? (
                        <>
                            {/* Varios LinkedIn: "LinkedIn ▾" abre un menú para elegir cuál abrir */}
                            <Box component="button" onClick={(e) => setMenuLinkedin(e.currentTarget)}
                                sx={{ ...estiloLink, gap: "1px", background: "none", border: 0, padding: 0, cursor: "pointer",
                                    font: "inherit", letterSpacing: "inherit" }}>
                                LinkedIn<ExpandMore sx={{ fontSize: 16 }} />
                            </Box>
                            {/* El menú se abre HACIA ARRIBA (sobre los botones redondos)
                                para que no se salga por la parte de abajo de la tarjeta */}
                            <Menu anchorEl={menuLinkedin} open={Boolean(menuLinkedin)} onClose={() => setMenuLinkedin(null)}
                                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                                transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                                slotProps={{ paper: { sx: { mt: "-6px", borderRadius: "12px", border: `1px solid ${COLORES.verde}`,
                                    boxShadow: "0 8px 24px rgba(0,0,0,.15)" } } }}
                                MenuListProps={{ dense: true, sx: { py: "4px" } }}>
                                {opcionesLinkedin.map((opcion) => (
                                    <MenuItem key={opcion.url} component="a" href={opcion.url} target="_blank" rel="noreferrer"
                                        onClick={() => setMenuLinkedin(null)}
                                        sx={{ fontFamily: "'Ubuntu', sans-serif", fontSize: 13, color: COLORES.texto,
                                            minHeight: "34px", justifyContent: "center",
                                            '&:hover': { backgroundColor: COLORES.verdeClaro } }}>
                                        {opcion.nombre}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    ) : (
                        /* Un solo LinkedIn: abre directo el de la empresa */
                        <Box component="a" href={LINKS.linkedin} target="_blank" rel="noreferrer" sx={estiloLink}>LinkedIn</Box>
                    )}
                    <Box component="a" href={LINKS.facebook} target="_blank" rel="noreferrer" sx={estiloLink}>Facebook</Box>
                    <Box component="a" href={LINKS.instagram} target="_blank" rel="noreferrer" sx={estiloLink}>Instagram</Box>
                </Box>

                {/* ---------- BOTÓN "CONOCE MÁS SOBRE NOSOTROS" ---------- */}
                <Box sx={{ margin: "28px 28px 28px" }}>
                    <Box component="a" href={LINKS.sitio} target="_blank" rel="noreferrer" sx={{
                        height: 46, borderRadius: "999px", backgroundColor: "#FFFFFF", border: `1.5px solid ${COLORES.verde}`,
                        display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 5px 0 22px",
                        fontSize: 13.5, fontWeight: 500, color: COLORES.texto, textDecoration: "none",
                        '&:hover': { backgroundColor: COLORES.verdeClaro },
                    }}>
                        Conoce más sobre nosotros
                        {/* Círculo verde con la flecha */}
                        <Box component="span" sx={{ width: 34, height: 34, borderRadius: "50%", backgroundColor: COLORES.verde,
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <ArrowForward sx={{ fontSize: 17 }} />
                        </Box>
                    </Box>
                </Box>

                {/* ---------- FRANJA "ÚNETE A NUESTRO EQUIPO" (pie de la tarjeta) ----------
                    Solo aparece si la tarjeta tiene vacantes={true}.
                    Franja verde muy clara pegada al borde de abajo: discreta, pero separada
                    del resto para que se note. Lleva a la bolsa de trabajo.
                    "mt: auto" la empuja hasta abajo cuando la tarjeta ocupa toda la pantalla (celular).
                    El "safe-area" deja espacio para la barra del navegador en iPhone.
                    Sin vacantes: solo queda un espacio en blanco al final de la tarjeta. */}
                {!vacantes ? (
                    <Box sx={{ height: { xs: "calc(28px + env(safe-area-inset-bottom))", sm: 28 } }} />
                ) : (
                <Box component="a" href={LINKS.vacantes} target="_blank" rel="noreferrer" sx={{
                    mt: "auto", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                    padding: { xs: "14px 28px calc(14px + env(safe-area-inset-bottom))", sm: "14px 28px" },
                    backgroundColor: COLORES.verdeClaro, borderTop: "1px solid #DDF3C8",
                    fontSize: 13, fontWeight: 500, color: COLORES.texto, textDecoration: "none", transition: "all .2s",
                    '&:hover': { backgroundColor: "#E6F8D4" },
                    // La flecha se mueve un poco a la derecha al pasar el mouse
                    '&:hover .flecha': { transform: "translateX(3px)" },
                }}>
                    <WorkOutline sx={{ fontSize: 17, color: COLORES.verdeOscuro }} />
                    Únete a nuestro equipo
                    <ArrowForward className="flecha" sx={{ fontSize: 15, color: COLORES.verdeOscuro, transition: "transform .2s" }} />
                </Box>
                )}
            </Box>
        </Box>
    )
}

export default CardMain;
